import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { getInfiniteQueryListNextParam } from '@/utils/list';

import {
  createTrashLog,
  deleteTrashLog,
  getTrashLogById,
  getTrashLogPdf,
  getTrashLogs,
  getTrashLogsByDate,
  signTrashLog,
  updateTrashLog,
} from './trash-log.api';
import type { CreateTrashLogFormValues, UpdateTrashLogFormValues } from './trash-log.dto';

const ROOT_KEY = 'trash-logs';

export const useTrashLogsQuery = (limit: number) =>
  useInfiniteQuery({
    queryKey: [ROOT_KEY, limit],
    queryFn: ({ pageParam }) => getTrashLogs(limit, pageParam),
    initialPageParam: 1,
    getNextPageParam: getInfiniteQueryListNextParam,
  });

export const useTrashLogByIdQuery = (id: string | null | undefined) =>
  useQuery({
    queryKey: [ROOT_KEY, 'by-id', id],
    queryFn: () => getTrashLogById(id!),
    enabled: !!id,
  });

export const useTrashLogsByDateQuery = (date: string | null | undefined) =>
  useQuery({
    queryKey: [ROOT_KEY, 'by-date', date],
    queryFn: () => getTrashLogsByDate(date!),
    enabled: !!date,
  });

export const useCreateTrashLogMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (values: CreateTrashLogFormValues) => createTrashLog(values),
    onSuccess(_, { date }) {
      queryClient.invalidateQueries({ queryKey: [ROOT_KEY] });
      queryClient.invalidateQueries({ queryKey: [ROOT_KEY, 'by-date', date] });
    },
  });
};

export const useUpdateTrashLogMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (values: UpdateTrashLogFormValues) => updateTrashLog(values),
    onSuccess(_, { id }) {
      queryClient.invalidateQueries({ queryKey: [ROOT_KEY] });
      queryClient.invalidateQueries({ queryKey: [ROOT_KEY, 'by-id', id] });
    },
  });
};

export const useDeleteTrashLogMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id }: { id: string }) => deleteTrashLog(id),
    onSuccess() {
      queryClient.invalidateQueries({ queryKey: [ROOT_KEY] });
    },
  });
};

export const useSignTrashLogMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id }: { id: string }) => signTrashLog(id),
    onSuccess(_, { id }) {
      queryClient.invalidateQueries({ queryKey: [ROOT_KEY] });
      queryClient.invalidateQueries({ queryKey: [ROOT_KEY, 'by-id', id] });
    },
  });
};

export const useTrashLogPdfMutation = () =>
  useMutation({
    mutationFn: ({ id, timezone }: { id: string; timezone?: string }) =>
      getTrashLogPdf(id, timezone),
  });
